import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { useSetRegistryCurrencyMutation } from '@graphql/mutations/currency.graphql.generated';
import { useGetCurrencyCodesQuery } from '@graphql/queries/currency.graphql.generated';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { useRegistryCurrencyCodes } from '@shared/utils/currency/useRegistryCurrencyCodes';
import { useRegistryCurrencyLock } from '@shared/hooks/useRegistryCurrencyLock';
import { useCallback, useEffect, useState } from 'react';

type CurrencyDialogControllerArgs = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  eventId: Maybe<string>;
  registryCurrency: Maybe<RegistryCurrencyCodesFragment>;
  setRegistryCurrency: Maybe<(c: RegistryCurrencyCodesFragment) => void>;
}>;

export const useCurrencyDialogController = ({ isOpen, onClose, eventId, registryCurrency: currentCurrency, setRegistryCurrency }: CurrencyDialogControllerArgs) => {
  const [setCurrency] = useSetRegistryCurrencyMutation();

  const {
    hasPayoutAccountLock: isCurrencyLocked,
    lockedCurrencyCode,
    isLoading: payoutLoading,
    hasError: hasPayoutAccountError,
    retry: retryPayoutAccount,
  } = useRegistryCurrencyLock(eventId, { skip: !isOpen });
  // No save until a fresh read succeeds, so a stale cached "no account" cannot offer one.
  const isPayoutAccountPending = payoutLoading || hasPayoutAccountError;

  const currencies = useRegistryCurrencyCodes();

  // Keep reference to the full currency list for finding fragments on save
  const { data: currencyCodeData } = useGetCurrencyCodesQuery();
  const currencyItems = currencyCodeData?.getRegistryStaticData?.currencies;

  const initialCode = (currentCurrency?.code ?? DEFAULT_CURRENCY) as CurrencyCodeType;
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<CurrencyCodeType | ''>(initialCode);

  useEffect(() => {
    setSelectedCurrencyCode((currentCurrency?.code ?? DEFAULT_CURRENCY) as CurrencyCodeType);
  }, [currentCurrency]);

  const handleOnSave = useCallback(
    (eventId: string, currencyCode: string) => {
      setCurrency({
        variables: {
          eventId,
          payload: { code: currencyCode },
        },
        refetchQueries: () => ['GetAdminRegistryData', 'PublicShopRoot'],
      });

      // Find the full fragment to notify parent (preserves existing API contract)
      if (setRegistryCurrency && currencyItems) {
        const allItems = [...(currencyItems.topRanked ?? []), ...(currencyItems.ordered ?? [])];
        const fragment = allItems.find(item => item.code === currencyCode);
        if (fragment) {
          setRegistryCurrency(fragment);
        }
      }
    },
    [setCurrency, setRegistryCurrency, currencyItems],
  );

  // When locked, force the selector to show the payout account's currency
  useEffect(() => {
    if (isCurrencyLocked && lockedCurrencyCode) {
      setSelectedCurrencyCode(lockedCurrencyCode as CurrencyCodeType);
    }
  }, [isCurrencyLocked, lockedCurrencyCode]);

  const onCancel = () => {
    setSelectedCurrencyCode(initialCode);
    onClose();
  };

  const onSave = useCallback(() => {
    if (isPayoutAccountPending) return;
    if (isCurrencyLocked) {
      onClose();
      return;
    }
    if (eventId && selectedCurrencyCode) {
      handleOnSave(eventId, selectedCurrencyCode);
    }
    onClose();
  }, [handleOnSave, onClose, eventId, selectedCurrencyCode, isCurrencyLocked, isPayoutAccountPending]);

  return {
    onCancel,
    onSave,
    currencies,
    selectedCurrencyCode,
    setSelectedCurrencyCode,
    isCurrencyLocked,
    isPayoutAccountPending,
    hasPayoutAccountError,
    retryPayoutAccount,
    lockedCurrencyCode,
  };
};
